import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Globe,
  Clock,
  Mail,
  User,
  MessageCircle,
  Smile,
  MessageSquare,
  Phone,
  Smartphone,
  Check,
  Rocket,
  Plus,
  Trash2,
  ShieldCheck,
  Pencil,
  Home,
  Sparkles,
  Loader2,
  Building2,
  Info,
  Briefcase,
  HardHat,
  Hammer,
  DollarSign,
  MapPin,
  Siren,
  AlertTriangle,
  Wrench,
  Palette,
  Calendar,
  Users,
  Scale,
  Target,
  FileText,
  Gavel,
  Sun,
} from "lucide-react";
import Sidebar from "@/components/Sidebar";
import Cookies from "js-cookie";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

// Local Storage Key (separate from real estate to avoid collisions)
const STORAGE_KEY = "ai_roofing_onboarding_data_v1";

/** UI-only visibility flags; data still loads/saves via formData. */
const ROOFING_UI_HIDDEN = {
  specialization_6_3: true,
  quotingWorkflowDescription_6_4: true,
  outOfHoursMessage_6_5: true,
  globalObjective_7_1: true,
  knownUserHandling_7_2: true,
  financing_9_5: true,
  ballparkPricing_9_6: true,
  supportRoles_13_1: true,
};

// Initial form state matching the expected backend schema for roofing_v1.
const initialFormData = {
  "knowledge_base": {
    "company_name": "",
    "trading_name": "",
    "founded_year": null,
    "website_url": "",
    "social_networks": [
      {
        "network": "instagram",
        "url": ""
      }
    ],
    "office_location": {
      "address": "",
      "is_customer_facing": true,
      "country": "",
      "state": ""
    },
    "service_area": {
      "description": "Any",
      "zip_codes": ["any"],
      "suburbs_or_lgas": "",
      "base_postcode": "",
      "max_drive_radius_km": 100,
      "crosses_state_borders": true,
      "regional_callouts": {
        "allowed": true,
        "min_job_value": ""
      }
    },
    "credentials": {
      "registration_number": "",
      "license_holder_name": "",
      "license_number": "#ROC12345",
      "insurance": "General Liability & Worker's Comp certified",
      "warranty_terms": "Limited Lifetime Warranty on asphalt shingle replacements"
    },
    "legal_and_compliance": {
      "privacy_policy_url": "",
      "state_regulations": "By state law, jobs over $5,000 include a 5-day cooling-off period."
    },
    "operations": {
      "office_hours": {
        "monday_to_friday": { "start": "08:30", "end": "17:00" },
        "saturday": { "start": "09:00", "end": "13:00" },
        "sunday": null,
        "out_of_hours_message": "Our office is currently closed..."
      },
      "field_crew_hours": {
        "monday_to_friday": { "start": "06:30", "end": "15:00" },
        "saturday": { "start": "07:00", "end": "12:00" },
        "sunday": null
      },
      "crew_capacity": {
        "concurrent_crews": 3,
        "specializations": [
          { "crew_name": "Crew Alpha", "focus": ["metal_roof_replacement", "new_build_roofing"] },
          { "crew_name": "Crew Beta", "focus": ["tile_roof_replacement", "roof_restoration_repaint_rebed"] },
          { "crew_name": "Repair Team", "focus": ["roof_repairs_leak_detection", "storm_hail_damage_repair"] }
        ]
      },
      "quoting_workflow": {
        "on_site_quoter_role": "Dedicated Estimator",
        "follow_up_booking_flow": "office_routing",
        "description": "The estimator provides the quote on-site, but actual repair dates are scheduled by the office manager afterwards."
      }
    },
    "handoff_settings": {
      "roles": [
        {
          "type": "Emergency",
          "role": "On-call Technician",
          "name": "",
          "priority": "critical",
          "phone": "",
          "allowed_channels": ["phone_call", "sms", "whatsapp", "telegram"],
          "availability_hours": "24/7",
          "after_hours_action": "route_to_on_call_number"
        },
        {
          "type": "warranty",
          "role": "Production Manager",
          "name": "",
          "priority": "high",
          "email": "",
          "allowed_channels": ["phone_call", "sms", "whatsapp", "telegram"],
          "availability_hours": "24/7",
          "after_hours_action": "route_to_on_call_number"
        },
        {
          "type": "commercial",
          "role": "Head of Commercial Roofing",
          "name": "",
          "min_project_size": "10,000 sq ft",
          "allowed_channels": ["phone_call", "sms", "whatsapp", "telegram"],
          "availability_hours": "24/7",
          "after_hours_action": "route_to_on_call_number"
        },
        {
          "type": "general",
          "role": "Office Manager",
          "name": "",
          "priority": "medium",
          "email": "",
          "allowed_channels": ["phone_call", "sms", "whatsapp", "telegram"],
          "availability_hours": "24/7",
          "after_hours_action": "route_to_on_call_number"
        }
      ],
      "handoff_triggers": [
        "human_requested_3_times",
        "complex_insurance_claim",
        "customer_complaint_or_angry"
      ],
      "handoff_messages": {
        "general_escalation": "I'll have one of our human specialists take over...",
        "emergency_escalation": "I'm routing you directly to our on-call technician..."
      }
    },
    "pricing_policy": {
      "quote_rules": {
        "free_quotes_standard": true
      },
      "job_requirements": {
        "minimum_job_value": "$500",
        "deposit_required": "10% upon acceptance"
      },
      "payment_and_finance": {
        "tax_status": "GST inclusive",
        "payment_methods": ["Bank Transfer", "Credit Card (Mastercard/Visa)", "Cash"],
        "financing_options": {
          "offered": true,
          "provider_name": "Brighte Finance"
        }
      },
      "ballpark_pricing": {
        "enforce_no_exact_prices": true,
        "products": [
          { "product_name": "asphalt_shingles", "pricing": "$400 - $600 per square" },
          { "product_name": "metal_roofing", "pricing": "$900 - $1500 per square" }
        ]
      }
    },
    "services_offered": {
      "metal_roof_replacement": "true",
      "tile_roof_replacement": "true",
      "slate_roof_work": "false",
      "roof_restoration_repaint_rebed": "sometimes",
      "roof_repairs_leak_detection": "true",
      "storm_hail_damage_repair": "true",
      "insurance_claim_work": "sometimes",
      "reroofing_over_existing": "false",
      "new_build_roofing": "true",
      "strata_multi_dwelling_roofing": "sometimes",
      "gutters_fascia_downpipes": "true",
      "gutter_guard": "sometimes",
      "skylights": "true",
      "whirlybirds_ventilation": "true",
      "roof_painting": "false",
      "solar_panel_removal_refit": "sometimes",
      "insulation": "false",
      "asbestos_roof_removal": "false",
      "heritage_specialist_work": "sometimes",
      "commercial_industrial_roofing": "true",
      "annual_maintenance": "true",
      "other_services": {
        "status": "true",
        "description": "Chimney flashing repairs, custom metal capping..."
      }
    },
    "objection_handling": [
      {
        "objection": "Price is too high / I found it cheaper",
        "response": "We use premium materials and never cut corners on waterproofing..."
      }
    ],
    "competitive_strategy": {
      "competitors_often_mentioned": ["Local Roofers Co", "Cheap Roofs AU"],
      "core_differentiator": "We provide a 10-year leak-free guarantee..."
    },
    "last_rag_index_update": "2026-04-25T10:00:00Z"
  },
  "qualification_logic": {
    "questions": [
      {
        "question": "What's the full address of the property?",
        "key": "property_address",
        "internal_goal": "Service area check and logistics planning",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "Are you the owner, tenant, or an agent for this property?",
        "key": "ownership_status",
        "internal_goal": "Identify decision-maker authority",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "Is this a house, townhouse, unit, commercial building, or strata property?",
        "key": "property_type",
        "internal_goal": "Determine equipment needs and scale",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "Is the property single, double, or triple storey?",
        "key": "storey_count",
        "internal_goal": "Determine ladder height and safety requirements",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "Do you know what material the roof is currently made of (e.g., tile, metal)?",
        "key": "roof_type",
        "internal_goal": "Match material with crew specialization",
        "turn_on": true,
        "optional": true
      },
      {
        "question": "Roughly how old is the roof?",
        "key": "roof_age",
        "internal_goal": "Identify potential for full replacement vs repair",
        "turn_on": true,
        "optional": true
      },
      {
        "question": "What specifically is happening with the roof? What do you need help with?",
        "key": "issue_details",
        "internal_goal": "Categorize the primary problem (leak, damage, aesthetic)",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "How urgent is this? Do you have an active leak now, or are you planning ahead?",
        "key": "timeline_urgency",
        "internal_goal": "Prioritize emergency callouts vs standard quotes",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "Has this been quoted before? If so, do you mind sharing by whom?",
        "key": "previous_quotes",
        "internal_goal": "Assess price sensitivity and competition",
        "turn_on": false,
        "optional": true
      },
      {
        "question": "Is there an insurance claim involved? If yes, what is the insurer's name and claim number?",
        "key": "insurance_status",
        "internal_goal": "Identify workflow (Assessor report vs private quote)",
        "turn_on": true,
        "optional": true
      },
      {
        "question": "Do you have any photos of the roof or the damage that you could upload?",
        "key": "has_photos",
        "internal_goal": "Gather visual evidence for remote estimation",
        "turn_on": true,
        "optional": true
      },
      {
        "question": "What is the best way to contact you (phone, SMS, or email)?",
        "key": "preferred_contact_method",
        "internal_goal": "Set communication channel for follow-up",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "What is the best time of day to reach you?",
        "key": "preferred_callback_time",
        "internal_goal": "Coordinate callback scheduling",
        "turn_on": true,
        "optional": false
      },
      {
        "question": "Just out of curiosity, how did you hear about us?",
        "key": "lead_source",
        "internal_goal": "Marketing source attribution",
        "turn_on": false,
        "optional": true
      },
      {
        "question": "Do you have a specific budget range in mind for this project?",
        "key": "budget_range",
        "internal_goal": "Qualify financial expectations",
        "turn_on": false,
        "optional": true
      }
    ],
    "unqualified_action": "manager",
    "strict_qualification_gate": true,
    "skip_qualification_if_known_user": true,
    "internal_goal_summary": "Qualify the lead's urgency, gather exact address...",
    "disqualification_rules": {
      "blocked_property_types": ["commercial", "strata"],
      "max_storeys": 2,
      "tenant_policy": "request_owner_details",
      "asbestos_policy": "refer_to_partner",
      "custom_auto_declines": ["Heritage listed buildings", "Thatch roofs"],
      "out_of_scope_services": [
        "Solar panel installation and cleaning",
        "Internal plumbing and pipe repairs",
        "Interior attic remodeling or insulation",
        "General house painting",
        "Landscaping and gardening",
        "HVAC and air conditioning repairs"
      ],
      "rejection_messages": {
        "out_of_area": "I appreciate you reaching out! Unfortunately, we don't service that specific area yet. We wish you the best of luck with your project!",
        "service_not_offered": "While we'd love to help, our crews don't currently handle that specific type of work.",
        "property_type": "Our team currently specializes exclusively in residential properties, so we are unable to take on this project.",
        "tenant_no_authority": "Since you are renting the property, we require direct authorization from the homeowner or property manager before we can provide an estimate. Could you have them reach out to us?",
        "asbestos_referral": "Based on the details, there might be asbestos present. While we aren't licensed for removal, we highly recommend contacting [Partner/Competitor Name] for specialized help.",
        "height_limit": "For safety and equipment reasons, our crews can only service buildings up to 2 storeys high.",
        "default_polite_no": "Thank you for considering, but it doesn't look like we are the right fit for your project at this time."
      }
    }
  },
  "scheduling": {
    "daylight_rule_enforced": true,
    "operating_hours": {
      "custom_schedule_rules": [
        "We do not quote on Fridays",
        "Owner is on-site Tuesdays only"
      ],
      "monday_to_friday": { "is_open": true, "start": "07:00", "end": "15:00" },
      "saturday": { "is_open": true, "start": "08:00", "end": "12:00" },
      "sunday": { "is_open": false, "start": null, "end": null },
      "public_holidays": {
        "is_open": true,
        "surcharge_fee": "$100"
      },
      "lunch_break": { "start": "12:00", "end": "12:30" }
    },
    "logistics": {
      "travel_buffer_minutes": 30,
      "appointment_time_format": "arrival_window",
      "window_size_hours": 2,
      "max_appointments_per_day": 5
    },
    "team_calendars": [
      {
        "staff_name": "Sales / Estimating Team",
        "calendar_id": "",
        "handles_appointment_types": [
          "free_quote_inspection",
          "restoration_consult",
          "phone_video_consult"
        ]
      },
      {
        "staff_name": "Repair Crew Alpha",
        "calendar_id": "",
        "handles_appointment_types": [
          "repair_booking",
          "emergency_leak_callout"
        ]
      },
      {
        "staff_name": "Michael Chen",
        "calendar_id": "",
        "handles_appointment_types": [
          "commercial_site_walk"
        ]
      }
    ],
    "booking_rules": {
      "min_lead_time_hours": 24,
      "max_lead_time_days": 28,
      "allow_double_booking": false,
      "weekend_auto_confirm": false
    },
    "booking_channel": {
      "visit_type": "free_quote_inspection",
      "call_type": "phone_video_consult"
    },
    "appointment_types": {
      "warranty_callback": {
        "is_offered": true,
        "duration_minutes": 60,
        "is_free": true,
        "attendee": "Production Manager",
        "prerequisites": ["original job details"]
      },
      "annual_maintenance_booking": {
        "is_offered": true,
        "duration_minutes": 120,
        "is_free": false,
        "price": "$299 flat fee",
        "attendee": "Maintenance Crew",
        "prerequisites": ["address"]
      },
      "free_quote_inspection": {
        "is_offered": true,
        "duration_minutes": 60,
        "is_free": true,
        "price": null,
        "attendee": "Sales Representative",
        "prerequisites": ["address", "problem description"]
      },
      "emergency_leak_callout": {
        "is_offered": true,
        "duration_minutes": 90,
        "is_free": true,
        "price": null,
        "attendee": "On-call Technician",
        "prerequisites": ["address", "phone", "photos"]
      },
      "insurance_assessment": {
        "is_offered": true,
        "duration_minutes": 60,
        "is_free": false,
        "price": "$150 report fee",
        "attendee": "Certified Assessor",
        "prerequisites": ["address", "insurance claim number"]
      },
      "repair_booking": {
        "is_offered": true,
        "duration_minutes": 120,
        "is_free": false,
        "price": "As per accepted quote",
        "attendee": "Crew Lead",
        "prerequisites": ["accepted quote reference"]
      },
      "restoration_consult": {
        "is_offered": true,
        "duration_minutes": 45,
        "is_free": true,
        "price": null,
        "attendee": "Restoration Specialist",
        "prerequisites": ["address", "age of roof"]
      },
      "commercial_site_walk": {
        "is_offered": true,
        "duration_minutes": 120,
        "is_free": true,
        "price": null,
        "attendee": "Head of Commercial",
        "prerequisites": ["site address", "square footage estimate"]
      },
      "phone_video_consult": {
        "is_offered": true,
        "duration_minutes": 30,
        "is_free": true,
        "price": null,
        "attendee": "Estimator",
        "prerequisites": ["phone number"]
      }
    }
  },
  "tone_of_voice": {
    "bot_name": "Jack",
    "disclose_ai_status": true,
    "style": "blue_collar_professional",
    "use_emojis": true,
    "use_aussie_slang": false,
    "forbidden_phrases": [
      "guarantee",
      "100% covered by insurance",
      "we promise",
      "insurance will definitely pay"
    ],
    "preferred_phrases": [
      "We've got your roof covered",
      "Built to withstand Aussie weather"
    ],
    "tagline": ""
  }
};

// URL validation helper
const validateUrl = (url) => {
  if (!url) return false;
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

// ============ Shared form primitives ============

const ProgressBar = ({ currentStep, totalSteps }) => {
  const progress = (currentStep / totalSteps) * 100;
  return (
    <div className="w-full" data-testid="progress-bar">
      <div className="flex justify-between items-center mb-2">
        <span className="font-mono text-[#00D1FF] text-sm">
          Step {currentStep} of {totalSteps}
        </span>
        <span className="font-mono text-[#9CA3AF] text-sm">
          {Math.round(progress)}% Complete
        </span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-[#00D1FF] to-[#00D1FF]/70 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const StepIndicator = ({ steps, currentStep }) => (
  <div className="hidden lg:flex items-center justify-center gap-2 mb-8 flex-wrap" data-testid="step-indicator">
    {steps.map((step, index) => {
      const stepNum = index + 1;
      const isActive = stepNum === currentStep;
      const isCompleted = stepNum < currentStep;
      return (
        <React.Fragment key={stepNum}>
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center font-mono text-xs transition-all duration-300 ${
                isActive
                  ? "bg-[#00D1FF] text-[#0F1115]"
                  : isCompleted
                  ? "bg-[#00D1FF]/20 text-[#00D1FF] border border-[#00D1FF]/50"
                  : "bg-white/5 text-[#9CA3AF] border border-white/10"
              }`}
            >
              {isCompleted ? <Check className="w-4 h-4" /> : stepNum}
            </div>
            <span
              className={`text-xs font-body hidden xl:block ${
                isActive ? "text-[#F3F4F6]" : "text-[#9CA3AF]"
              }`}
            >
              {step.shortTitle}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div className={`w-4 h-0.5 ${isCompleted ? "bg-[#00D1FF]/50" : "bg-white/10"}`} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

const FormInput = ({ label, required, error, icon: Icon, sublabel, ...props }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    {sublabel && <p className="text-[#9CA3AF] text-xs font-body -mt-1 ml-6">{sublabel}</p>}
    <input
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

const FormTextarea = ({ label, required, error, icon: Icon, sublabel, rows = 3, ...props }) => (
  <div className="space-y-2">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    {sublabel && <p className="text-[#9CA3AF] text-xs font-body -mt-1 ml-6">{sublabel}</p>}
    <textarea
      rows={rows}
      {...props}
      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body resize-y ${
        error
          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
      }`}
    />
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

const ToggleSwitch = ({ label, description, checked, onChange, icon: Icon }) => (
  <div className="flex items-start justify-between gap-4 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-all">
    <div className="flex items-start gap-3 flex-1">
      {Icon && <Icon className="w-5 h-5 text-[#00D1FF] mt-0.5" />}
      <div className="flex-1">
        <span className="text-[#F3F4F6] text-sm font-body">{label}</span>
        {description && (
          <p className="text-[#9CA3AF] text-xs font-body mt-1">{description}</p>
        )}
      </div>
    </div>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
        checked ? "bg-[#00D1FF]" : "bg-white/20"
      }`}
      aria-pressed={checked}
    >
      <div
        className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
          checked ? "translate-x-7" : "translate-x-1"
        }`}
      />
    </button>
  </div>
);

const RadioGroup = ({ label, required, options, value, onChange, error, icon: Icon }) => (
  <div className="space-y-3">
    <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
      {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
      {label}
      {required && <span className="text-[#00D1FF]">*</span>}
    </label>
    <div className="space-y-2">
      {options.map((option) => (
        <label
          key={option.value}
          className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
            value === option.value
              ? "bg-[#00D1FF]/10 border-[#00D1FF]/50"
              : "bg-white/5 border-white/10 hover:border-white/20"
          }`}
        >
          <input
            type="radio"
            name={label}
            value={option.value}
            checked={value === option.value}
            onChange={(e) => onChange(e.target.value)}
            className="sr-only"
          />
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
              value === option.value ? "border-[#00D1FF] bg-[#00D1FF]" : "border-white/30"
            }`}
          >
            {value === option.value && <div className="w-2 h-2 rounded-full bg-[#0F1115]" />}
          </div>
          <div className="flex flex-col">
            <span className="text-[#F3F4F6] text-sm font-body">{option.label}</span>
            {option.description && (
              <span className="text-[#9CA3AF] text-xs font-body mt-1">{option.description}</span>
            )}
          </div>
        </label>
      ))}
    </div>
    {error && <p className="text-red-400 text-xs font-body">{error}</p>}
  </div>
);

// Tri-state Yes / No / Sometimes picker used for services_offered
const TriStatePicker = ({ label, value, onChange }) => {
  const opts = [
    { v: "true", label: "Yes", cls: "bg-emerald-500/15 border-emerald-400/50 text-emerald-300" },
    { v: "sometimes", label: "Sometimes", cls: "bg-amber-500/15 border-amber-400/50 text-amber-300" },
    { v: "false", label: "No", cls: "bg-red-500/15 border-red-400/50 text-red-300" },
  ];
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
      <span className="text-[#F3F4F6] text-sm font-body flex-1">{label}</span>
      <div className="flex gap-1.5 flex-shrink-0">
        {opts.map((o) => {
          const active = (value || "false") === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => onChange(o.v)}
              className={`px-3 py-1.5 rounded-md text-xs font-body border transition-all ${
                active
                  ? o.cls
                  : "bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Dynamic string list (rows with trash buttons + add button)
const StringListEditor = ({ label, sublabel, values = [], onChange, placeholder, icon: Icon }) => (
  <div className="space-y-2">
    {label && (
      <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
        {Icon && <Icon className="w-4 h-4 text-[#00D1FF]" />}
        {label}
      </label>
    )}
    {sublabel && <p className="text-[#9CA3AF] text-xs font-body -mt-1 ml-6">{sublabel}</p>}
    <div className="space-y-2">
      {values.map((item, idx) => (
        <div key={idx} className="flex items-center gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => onChange(values.map((v, i) => (i === idx ? e.target.value : v)))}
            placeholder={placeholder}
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50 transition-all font-body text-sm"
          />
          <button
            type="button"
            onClick={() => onChange(values.filter((_, i) => i !== idx))}
            className="text-[#9CA3AF] hover:text-red-400 transition-colors p-2"
            aria-label="Remove"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
    <button
      type="button"
      onClick={() => onChange([...(values || []), ""])}
      className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
    >
      <Plus className="w-4 h-4" />
      Add
    </button>
  </div>
);

// Allowed-channels multi-select chip group
const CHANNEL_OPTIONS = [
  { value: "phone_call", label: "Phone Call", icon: Phone },
  { value: "sms", label: "SMS", icon: MessageSquare },
  { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
  { value: "telegram", label: "Telegram", icon: Smartphone },
];

const AllowedChannelsPicker = ({ value = [], onChange, label = "Allowed Channels" }) => (
  <div className="space-y-1.5">
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-[#D1D5DB] font-body">{label}</label>
      <div className="group relative">
        <Info className="w-3.5 h-3.5 text-[#6B7280] cursor-help" />
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-[#1F2937] text-[#D1D5DB] text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none w-64 z-50 border border-white/10 shadow-xl">
          Channels the AI may use when escalating this scenario to a human.
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {CHANNEL_OPTIONS.map((channel) => {
        const isSelected = value.includes(channel.value);
        const ChannelIcon = channel.icon;
        return (
          <button
            key={channel.value}
            type="button"
            onClick={() => {
              const next = isSelected
                ? value.filter((c) => c !== channel.value)
                : [...value, channel.value];
              onChange(next);
            }}
            className={`flex items-center gap-2.5 px-4 py-3 rounded-xl border transition-all duration-200 text-sm font-body ${
              isSelected
                ? "border-[#D4AF37]/60 bg-[#D4AF37]/10 text-[#D4AF37]"
                : "border-white/10 bg-white/[0.03] text-[#9CA3AF] hover:border-white/20 hover:bg-white/[0.05]"
            }`}
          >
            <ChannelIcon className={`w-4 h-4 ${isSelected ? "text-[#D4AF37]" : "text-[#6B7280]"}`} />
            <span>{channel.label}</span>
            {isSelected && <Check className="w-3.5 h-3.5 ml-auto text-[#D4AF37]" />}
          </button>
        );
      })}
    </div>
  </div>
);

const HiddenUi = ({ hidden, children }) =>
  hidden ? (
    <div className="hidden" aria-hidden="true">
      {children}
    </div>
  ) : (
    children
  );

// Section heading helper
const SectionHeading = ({ title, description }) => (
  <div className="mb-6">
    <h2 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2">{title}</h2>
    {description && <p className="text-[#9CA3AF] font-body">{description}</p>}
  </div>
);

const SubHeading = ({ title }) => (
  <h3 className="text-[#F3F4F6] text-sm font-body font-semibold uppercase tracking-wide mt-6 mb-3 pb-2 border-b border-white/10">
    {title}
  </h3>
);

// Day-window editor: { is_open, start, end } OR { start, end } variants
const DayWindowRow = ({ label, dayValue, onChange, showIsOpen = true }) => {
  const val = dayValue || {};
  return (
    <div className="flex flex-wrap items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
      <span className="text-[#F3F4F6] text-sm font-body w-32 flex-shrink-0">{label}</span>
      {showIsOpen && (
        <button
          type="button"
          onClick={() => onChange({ ...val, is_open: !val.is_open })}
          className={`relative w-11 h-6 rounded-full transition-colors duration-300 flex-shrink-0 ${
            val.is_open ? "bg-[#00D1FF]" : "bg-white/20"
          }`}
          aria-pressed={!!val.is_open}
        >
          <div
            className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform duration-300 ${
              val.is_open ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      )}
      <input
        type="time"
        value={val.start || ""}
        onChange={(e) => onChange({ ...val, start: e.target.value })}
        disabled={showIsOpen && !val.is_open}
        className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-40"
      />
      <span className="text-[#9CA3AF] text-sm">–</span>
      <input
        type="time"
        value={val.end || ""}
        onChange={(e) => onChange({ ...val, end: e.target.value })}
        disabled={showIsOpen && !val.is_open}
        className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] text-sm font-body focus:outline-none focus:border-[#00D1FF]/50 disabled:opacity-40"
      />
    </div>
  );
};

// ==================== STEPS ====================

// ------------------------------------------------------------
// Step 1: Company Profile & Credentials
// ------------------------------------------------------------
const Step1Company = ({ formData, setFormData, errors }) => {
  const kb = formData.knowledge_base;
  const updateKB = (field, value) =>
    setFormData({ ...formData, knowledge_base: { ...kb, [field]: value } });
  const updateCred = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...kb,
        credentials: { ...kb.credentials, [field]: value },
      },
    });
  const updateOffice = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...kb,
        office_location: { ...kb.office_location, [field]: value },
      },
    });

  const socials = kb.social_networks || [];
  const setSocials = (next) => updateKB("social_networks", next);

  return (
    <div className="space-y-6" data-testid="roofing-step-1">
      <SectionHeading
        title="Company Profile & Credentials"
        description="Core company details and proof of qualifications the Digital Brain uses when introducing your business."
      />

      <SubHeading title="1.1 Company Names" />
      <FormInput
        label="Official Legal Name"
        sublabel="Used for legal / formal correspondence."
        required
        type="text"
        placeholder="e.g., Oz Exteriors Roofing Group Pty Ltd"
        value={kb.company_name}
        onChange={(e) => updateKB("company_name", e.target.value)}
        error={errors.company_name}
        icon={Building2}
        data-testid="roofing-company-name-input"
      />
      <FormInput
        label="Trading Name"
        sublabel="What the Digital Brain uses when presenting to customers."
        type="text"
        placeholder="e.g., Oz Exteriors Roofing"
        value={kb.trading_name || ""}
        onChange={(e) => updateKB("trading_name", e.target.value)}
        icon={Briefcase}
      />

      <SubHeading title="1.2 Registration Number" />
      <FormInput
        label="ABN / EIN / Registration Number"
        sublabel="Used when responding to B2B client inquiries."
        type="text"
        placeholder="e.g., 12 345 678 901"
        value={kb.credentials?.registration_number || ""}
        onChange={(e) => updateCred("registration_number", e.target.value)}
        icon={FileText}
      />

      <SubHeading title="1.3 Licensing" />
      <FormInput
        label="Contractor License Number"
        type="text"
        placeholder="#ROC12345"
        value={kb.credentials?.license_number || ""}
        onChange={(e) => updateCred("license_number", e.target.value)}
        icon={ShieldCheck}
      />
      <FormInput
        label="License Holder Name"
        type="text"
        placeholder="e.g., Marcus Thorp"
        value={kb.credentials?.license_holder_name || ""}
        onChange={(e) => updateCred("license_holder_name", e.target.value)}
        icon={User}
      />

      <SubHeading title="1.4 Year Founded" />
      <FormInput
        label="Year Founded"
        sublabel="Used by the Digital Brain to build trust and handle price objections."
        type="number"
        min={1800}
        max={new Date().getFullYear()}
        placeholder="e.g., 2008"
        value={kb.founded_year || ""}
        onChange={(e) =>
          updateKB("founded_year", e.target.value ? parseInt(e.target.value, 10) : null)
        }
        icon={Calendar}
      />

      <SubHeading title="1.5 Website URL" />
      <FormInput
        label="Website URL"
        sublabel="Link customers can visit to review your portfolio."
        type="url"
        placeholder="https://your-roofing-company.com"
        value={kb.website_url || ""}
        onChange={(e) => updateKB("website_url", e.target.value)}
        error={errors.website_url}
        icon={Globe}
      />

      <SubHeading title="1.6 Social Media" />
      <p className="text-[#9CA3AF] text-xs font-body -mt-1">
        Links (Instagram, Facebook, etc.) the Digital Brain can share to redirect leads.
      </p>
      <div className="space-y-3">
        {socials.map((s, idx) => (
          <div key={idx} className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[#00D1FF] text-xs">Social #{idx + 1}</span>
              <button
                type="button"
                onClick={() => setSocials(socials.filter((_, i) => i !== idx))}
                className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              <input
                type="text"
                value={s.network || ""}
                onChange={(e) =>
                  setSocials(socials.map((it, i) => (i === idx ? { ...it, network: e.target.value } : it)))
                }
                placeholder="Network (e.g., instagram)"
                className="md:col-span-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
              <input
                type="url"
                value={s.url || ""}
                onChange={(e) =>
                  setSocials(socials.map((it, i) => (i === idx ? { ...it, url: e.target.value } : it)))
                }
                placeholder="https://instagram.com/yourcompany"
                className="md:col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
            </div>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setSocials([...(socials || []), { network: "", url: "" }])}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Social Network
      </button>

      <SubHeading title="1.7 Office & Physical Address" />
      <FormTextarea
        label="Office Address"
        sublabel="Your primary business address."
        rows={2}
        placeholder="123 Main Street, Wollongong NSW 2500"
        value={kb.office_location?.address || ""}
        onChange={(e) => updateOffice("address", e.target.value)}
        icon={MapPin}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Country"
          sublabel="Country where your office is located."
          placeholder="e.g., Australia"
          value={kb.office_location?.country || ""}
          onChange={(e) => updateOffice("country", e.target.value)}
          icon={Globe}
        />
        <FormInput
          label="State / Region"
          sublabel="State, province or region of your office."
          placeholder="e.g., NSW"
          value={kb.office_location?.state || ""}
          onChange={(e) => updateOffice("state", e.target.value)}
          icon={MapPin}
        />
      </div>
      <ToggleSwitch
        label="Is this a customer-facing address?"
        description="If enabled, the Digital Brain may share this address with customers who ask."
        checked={!!kb.office_location?.is_customer_facing}
        onChange={(v) => updateOffice("is_customer_facing", v)}
        icon={Home}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 2: Service Area
// ------------------------------------------------------------
const Step2ServiceArea = ({ formData, setFormData }) => {
  const sa = formData.knowledge_base.service_area || {};
  const update = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        service_area: { ...sa, [field]: value },
      },
    });
  const updateRegional = (field, value) =>
    update("regional_callouts", { ...(sa.regional_callouts || {}), [field]: value });

  const zipInput = (sa.zip_codes || []).join(", ");

  return (
    <div className="space-y-6" data-testid="roofing-step-2">
      <SectionHeading
        title="Service Area"
        description="Where you operate and how far you're willing to travel."
      />

      <SubHeading title="2.1 Coverage Area" />
      <FormInput
        label="Zip Codes / Postcodes"
        sublabel="Comma-separated list (use 'any' to cover everything)."
        type="text"
        placeholder="e.g., 2500, 2505, 2508 or 'any'"
        value={zipInput}
        onChange={(e) =>
          update(
            "zip_codes",
            e.target.value
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          )
        }
        icon={MapPin}
      />
      <FormTextarea
        label="Suburbs or LGAs"
        sublabel="List of suburbs, council areas or regions you serve."
        rows={2}
        placeholder="e.g., Wollongong, Shellharbour, Sutherland Shire"
        value={sa.suburbs_or_lgas || ""}
        onChange={(e) => update("suburbs_or_lgas", e.target.value)}
        icon={MapPin}
      />
      <FormTextarea
        label="Description"
        sublabel="Human-friendly summary of the coverage area."
        rows={2}
        placeholder="Illawarra and South Sydney regions"
        value={sa.description || ""}
        onChange={(e) => update("description", e.target.value)}
        icon={Info}
      />

      <SubHeading title="2.2 Base Postcode" />
      <FormInput
        label="Base / Depot Postcode"
        sublabel="Used to calculate drive distance and logistics."
        type="text"
        placeholder="e.g., 2500"
        value={sa.base_postcode || ""}
        onChange={(e) => update("base_postcode", e.target.value)}
        icon={Home}
      />

      <SubHeading title="2.3 Maximum Radius" />
      <FormInput
        label="Maximum Drive Radius (km)"
        sublabel="Beyond this distance the Digital Brain auto-declines."
        type="number"
        min={0}
        placeholder="100"
        value={sa.max_drive_radius_km || ""}
        onChange={(e) =>
          update("max_drive_radius_km", e.target.value ? parseInt(e.target.value, 10) : 0)
        }
        icon={Target}
      />

      <SubHeading title="2.4 State Boundaries" />
      <ToggleSwitch
        label="Cross state / region borders"
        description="Enable if jobs across neighboring regions are allowed."
        checked={!!sa.crosses_state_borders}
        onChange={(v) => update("crosses_state_borders", v)}
        icon={Scale}
      />

      <SubHeading title="2.5 Regional Callouts" />
      <ToggleSwitch
        label="Allow regional callouts"
        description="Do you travel far outside the city for larger jobs?"
        checked={!!sa.regional_callouts?.allowed}
        onChange={(v) => updateRegional("allowed", v)}
        icon={MapPin}
      />
      {sa.regional_callouts?.allowed && (
        <FormInput
          label="Minimum Job Value for Regional Callouts"
          sublabel="Only travel if the job is worth at least this amount."
          type="text"
          placeholder="e.g., $10,000"
          value={sa.regional_callouts?.min_job_value || ""}
          onChange={(e) => updateRegional("min_job_value", e.target.value)}
          icon={DollarSign}
        />
      )}
    </div>
  );
};

// ------------------------------------------------------------
// Step 3: Services Offered
// ------------------------------------------------------------
const SERVICE_GROUPS = [
  {
    title: "3.1 Core Roofing Services",
    items: [
      { key: "metal_roof_replacement", label: "Metal roof replacement" },
      { key: "tile_roof_replacement", label: "Tile roof replacement" },
      { key: "slate_roof_work", label: "Slate roof work" },
      { key: "roof_restoration_repaint_rebed", label: "Roof restoration (repaint / re-bed)" },
      { key: "roof_repairs_leak_detection", label: "Roof repairs & leak detection" },
      { key: "storm_hail_damage_repair", label: "Storm / hail damage repair" },
      { key: "insurance_claim_work", label: "Insurance claim work" },
      { key: "reroofing_over_existing", label: "Re-roofing over existing roof" },
      { key: "new_build_roofing", label: "New-build roofing" },
      { key: "strata_multi_dwelling_roofing", label: "Strata / multi-dwelling roofing" },
    ],
  },
  {
    title: "3.2 Additional Services",
    items: [
      { key: "gutters_fascia_downpipes", label: "Gutters, fascia & downpipes" },
      { key: "gutter_guard", label: "Gutter guard" },
      { key: "skylights", label: "Skylights" },
      { key: "whirlybirds_ventilation", label: "Whirlybirds / ventilation" },
      { key: "roof_painting", label: "Roof painting" },
      { key: "solar_panel_removal_refit", label: "Solar panel removal & refit" },
      { key: "insulation", label: "Insulation" },
      { key: "asbestos_roof_removal", label: "Asbestos roof removal" },
      { key: "heritage_specialist_work", label: "Heritage specialist work" },
      { key: "commercial_industrial_roofing", label: "Commercial / industrial roofing" },
      { key: "annual_maintenance", label: "Annual maintenance" },
    ],
  },
];

const Step3Services = ({ formData, setFormData }) => {
  const services = formData.knowledge_base.services_offered || {};
  const updateService = (key, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        services_offered: { ...services, [key]: value },
      },
    });
  const updateOther = (field, value) =>
    updateService("other_services", { ...(services.other_services || {}), [field]: value });

  const oos =
    formData.qualification_logic?.disqualification_rules?.out_of_scope_services || [];
  const setOOS = (next) =>
    setFormData({
      ...formData,
      qualification_logic: {
        ...formData.qualification_logic,
        disqualification_rules: {
          ...formData.qualification_logic.disqualification_rules,
          out_of_scope_services: next,
        },
      },
    });

  return (
    <div className="space-y-6" data-testid="roofing-step-3">
      <SectionHeading
        title="Services Offered"
        description="Your competency matrix — what you do, what you don't, and when."
      />

      {SERVICE_GROUPS.map((group) => (
        <div key={group.title}>
          <SubHeading title={group.title} />
          <div className="space-y-2">
            {group.items.map((it) => (
              <TriStatePicker
                key={it.key}
                label={it.label}
                value={services[it.key]}
                onChange={(v) => updateService(it.key, v)}
              />
            ))}
          </div>
        </div>
      ))}

      <SubHeading title="3.3 Specialized Skills" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Describe any niche capabilities you want the Digital Brain to highlight.
      </p>
      <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
        <span className="text-[#F3F4F6] text-sm font-body">Offer other specialized services?</span>
        <TriStateInline
          value={services.other_services?.status}
          onChange={(v) => updateOther("status", v)}
        />
      </div>
      <FormTextarea
        label="Specialized Services Description"
        rows={3}
        placeholder="e.g., Chimney flashing repairs, custom metal capping, box-gutter replacements"
        value={services.other_services?.description || ""}
        onChange={(e) => updateOther("description", e.target.value)}
        icon={Hammer}
      />

      <SubHeading title="3.4 Out-of-Scope Services" />
      <StringListEditor
        label="Services you never provide"
        sublabel="Used by the Digital Brain for instant disqualification."
        values={oos}
        onChange={setOOS}
        placeholder="e.g., Solar panel installation"
        icon={AlertTriangle}
      />
    </div>
  );
};

// Small inline tri-state (Yes / Sometimes / No)
const TriStateInline = ({ value, onChange }) => {
  const opts = [
    { v: "true", label: "Yes" },
    { v: "sometimes", label: "Sometimes" },
    { v: "false", label: "No" },
  ];
  return (
    <div className="flex gap-1.5">
      {opts.map((o) => {
        const active = (value || "false") === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={`px-3 py-1.5 rounded-md text-xs font-body border transition-all ${
              active
                ? "bg-[#00D1FF]/15 border-[#00D1FF]/50 text-[#00D1FF]"
                : "bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
};

// ------------------------------------------------------------
// Step 4: Appointment Types & Booking Rules
// ------------------------------------------------------------
const APPOINTMENT_TYPE_LABELS = {
  free_quote_inspection: "Free Quote / Inspection",
  emergency_leak_callout: "Emergency Leak Response",
  insurance_assessment: "Insurance Assessment",
  repair_booking: "Repair Booking",
  restoration_consult: "Restoration Consultation",
  commercial_site_walk: "Commercial Site Walk",
  phone_video_consult: "Phone / Video Consult",
  warranty_callback: "Warranty Callback",
  annual_maintenance_booking: "Annual Maintenance Booking",
};

const CALL_TYPE_KEY_RE = /phone|consult|callback/i;
const VISIT_TYPE_KEY_RE = /inspection|quote|site_walk|site/i;

const Step4Appointments = ({ formData, setFormData }) => {
  const apps = formData.scheduling.appointment_types || {};
  const rules = formData.scheduling.booking_rules || {};
  const bookingChannel = formData.scheduling.booking_channel || {
    visit_type: "free_quote_inspection",
    call_type: "phone_video_consult",
  };

  const offeredTypeKeys = Object.keys(apps).filter((key) => apps[key]?.is_offered !== false);
  const visitTypeOptions = offeredTypeKeys.filter((key) => VISIT_TYPE_KEY_RE.test(key));
  const callTypeOptions = offeredTypeKeys.filter(
    (key) => CALL_TYPE_KEY_RE.test(key) && key !== "warranty_callback"
  );

  const updateBookingChannel = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...formData.scheduling,
        booking_channel: {
          ...bookingChannel,
          [field]: value,
        },
      },
    });

  const updateApp = (key, field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...formData.scheduling,
        appointment_types: {
          ...apps,
          [key]: { ...(apps[key] || {}), [field]: value },
        },
      },
    });

  const updateRules = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...formData.scheduling,
        booking_rules: { ...rules, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="roofing-step-4">
      <SectionHeading
        title="Appointment Types & Booking Rules"
        description="Configure which visit types the Digital Brain can offer and the rules around scheduling them."
      />

      <SubHeading title="4.1 & 4.2 Appointment Menu & Booking Parameters" />
      <div className="space-y-4">
        {Object.keys(apps).map((key) => {
          const a = apps[key] || {};
          const label = APPOINTMENT_TYPE_LABELS[key] || key;
          return (
            <div
              key={key}
              className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-3"
            >
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h4 className="text-[#F3F4F6] text-sm font-heading font-bold">{label}</h4>
                  <p className="text-[#9CA3AF] text-xs font-body">{key}</p>
                </div>
                <button
                  type="button"
                  onClick={() => updateApp(key, "is_offered", !a.is_offered)}
                  className={`relative w-11 h-6 rounded-full transition-colors ${
                    a.is_offered ? "bg-[#00D1FF]" : "bg-white/20"
                  }`}
                  aria-pressed={!!a.is_offered}
                >
                  <div
                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                      a.is_offered ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>

              {a.is_offered && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <FormInput
                    label="Duration (minutes)"
                    type="number"
                    min={0}
                    value={a.duration_minutes || ""}
                    onChange={(e) =>
                      updateApp(
                        key,
                        "duration_minutes",
                        e.target.value ? parseInt(e.target.value, 10) : 0
                      )
                    }
                    icon={Clock}
                  />
                  <FormInput
                    label="Attendee / Assigned Staff"
                    type="text"
                    placeholder="e.g., Sales Representative"
                    value={a.attendee || ""}
                    onChange={(e) => updateApp(key, "attendee", e.target.value)}
                    icon={User}
                  />
                  <div className="md:col-span-2">
                    <ToggleSwitch
                      label="Is this appointment free?"
                      checked={!!a.is_free}
                      onChange={(v) => updateApp(key, "is_free", v)}
                      icon={DollarSign}
                    />
                  </div>
                  {!a.is_free && (
                    <div className="md:col-span-2">
                      <FormInput
                        label="Price / Fee"
                        type="text"
                        placeholder="e.g., $150 report fee"
                        value={a.price || ""}
                        onChange={(e) => updateApp(key, "price", e.target.value)}
                        icon={DollarSign}
                      />
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <StringListEditor
                      label="Prerequisites"
                      sublabel="Required info before booking (address, photos, etc.)"
                      values={a.prerequisites || []}
                      onChange={(next) => updateApp(key, "prerequisites", next)}
                      placeholder="e.g., address"
                      icon={FileText}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <SubHeading title="4.2a Booking Channel Mapping" />
      <p className="text-sm text-[#9CA3AF] font-body -mt-2 mb-4">
        Used when a customer declines a site visit or requests a phone callback (e.g. new build
        not ready for inspection).
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
        <div>
          <label className="block text-sm font-medium text-[#F3F4F6] mb-2">
            Default visit type
          </label>
          <select
            value={bookingChannel.visit_type || ""}
            onChange={(e) => updateBookingChannel("visit_type", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-[#F3F4F6] text-sm focus:outline-none focus:border-[#00D1FF]/50"
          >
            {(visitTypeOptions.length ? visitTypeOptions : offeredTypeKeys).map((key) => (
              <option key={key} value={key} className="bg-[#1a1d21]">
                {APPOINTMENT_TYPE_LABELS[key] || key}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-[#F3F4F6] mb-2">
            Phone consult type
          </label>
          <select
            value={bookingChannel.call_type || ""}
            onChange={(e) => updateBookingChannel("call_type", e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-[#F3F4F6] text-sm focus:outline-none focus:border-[#00D1FF]/50"
          >
            {(callTypeOptions.length ? callTypeOptions : offeredTypeKeys).map((key) => (
              <option key={key} value={key} className="bg-[#1a1d21]">
                {APPOINTMENT_TYPE_LABELS[key] || key}
              </option>
            ))}
          </select>
        </div>
      </div>

      <SubHeading title="4.3 Min / Max Lead Time" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormInput
          label="Minimum Lead Time (hours)"
          sublabel="No bookings sooner than X hours from now."
          type="number"
          min={0}
          value={rules.min_lead_time_hours || ""}
          onChange={(e) =>
            updateRules(
              "min_lead_time_hours",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Clock}
        />
        <FormInput
          label="Maximum Lead Time (days)"
          sublabel="No bookings further than Y days in the future."
          type="number"
          min={0}
          value={rules.max_lead_time_days || ""}
          onChange={(e) =>
            updateRules(
              "max_lead_time_days",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Calendar}
        />
      </div>

      <SubHeading title="4.4 Overbooking" />
      <ToggleSwitch
        label="Allow double-booking a slot"
        description="Permit overlapping appointments for the same staff member."
        checked={!!rules.allow_double_booking}
        onChange={(v) => updateRules("allow_double_booking", v)}
        icon={Users}
      />

      <SubHeading title="4.5 Weekends" />
      <ToggleSwitch
        label="Weekend bookings auto-confirmed"
        description="If disabled, weekend bookings stay pending for manual approval."
        checked={!!rules.weekend_auto_confirm}
        onChange={(v) => updateRules("weekend_auto_confirm", v)}
        icon={Calendar}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 5: Booking Calendar — Hours & Availability
// ------------------------------------------------------------
const Step5Calendar = ({ formData, setFormData }) => {
  const sch = formData.scheduling || {};
  const oh = sch.operating_hours || {};
  const logistics = sch.logistics || {};

  const updateOH = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        operating_hours: { ...oh, [field]: value },
      },
    });
  const updateLogistics = (field, value) =>
    setFormData({
      ...formData,
      scheduling: {
        ...sch,
        logistics: { ...logistics, [field]: value },
      },
    });
  const updateSch = (field, value) =>
    setFormData({ ...formData, scheduling: { ...sch, [field]: value } });

  return (
    <div className="space-y-6" data-testid="roofing-step-5">
      <SectionHeading
        title="Booking Calendar — Hours & Availability"
        description="Define when customers can book appointments and the rules around them."
      />

      <SubHeading title="5.1 Appointment Hours" />
      <div className="space-y-2">
        <DayWindowRow
          label="Mon – Fri"
          dayValue={oh.monday_to_friday}
          onChange={(v) => updateOH("monday_to_friday", v)}
        />
        <DayWindowRow
          label="Saturday"
          dayValue={oh.saturday}
          onChange={(v) => updateOH("saturday", v)}
        />
        <DayWindowRow
          label="Sunday"
          dayValue={oh.sunday}
          onChange={(v) => updateOH("sunday", v)}
        />
      </div>

      <SubHeading title="5.2 Public Holidays" />
      <ToggleSwitch
        label="Open on public holidays"
        checked={!!oh.public_holidays?.is_open}
        onChange={(v) =>
          updateOH("public_holidays", { ...(oh.public_holidays || {}), is_open: v })
        }
        icon={Calendar}
      />
      {oh.public_holidays?.is_open && (
        <FormInput
          label="Holiday Surcharge Fee"
          sublabel="Extra fee added to bookings on holidays."
          type="text"
          placeholder="e.g., $100"
          value={oh.public_holidays?.surcharge_fee || ""}
          onChange={(e) =>
            updateOH("public_holidays", {
              ...(oh.public_holidays || {}),
              surcharge_fee: e.target.value,
            })
          }
          icon={DollarSign}
        />
      )}

      <SubHeading title="5.3 Breaks" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Block-out lunch / break so the Digital Brain doesn't schedule during this window.
      </p>
      <DayWindowRow
        label="Lunch break"
        dayValue={oh.lunch_break}
        onChange={(v) => updateOH("lunch_break", v)}
        showIsOpen={false}
      />

      <SubHeading title="5.4 Daylight Rule" />
      <ToggleSwitch
        label="Enforce daylight rule"
        description="Block roof inspections during dark hours for safety."
        checked={!!sch.daylight_rule_enforced}
        onChange={(v) => updateSch("daylight_rule_enforced", v)}
        icon={Sun}
      />

      <SubHeading title="5.5 Travel Buffer" />
      <FormInput
        label="Travel Buffer (minutes)"
        sublabel="Time automatically added between back-to-back appointments."
        type="number"
        min={0}
        value={logistics.travel_buffer_minutes || ""}
        onChange={(e) =>
          updateLogistics(
            "travel_buffer_minutes",
            e.target.value ? parseInt(e.target.value, 10) : 0
          )
        }
        icon={Clock}
      />

      <SubHeading title="5.6 Time Format" />
      <RadioGroup
        label="How should the Digital Brain quote times?"
        options={[
          {
            value: "exact_time",
            label: "Exact time",
            description: "e.g., 10:00 AM sharp",
          },
          {
            value: "arrival_window",
            label: "Arrival window",
            description: "e.g., Between 10:00 and 12:00",
          },
        ]}
        value={logistics.appointment_time_format || "arrival_window"}
        onChange={(v) => updateLogistics("appointment_time_format", v)}
        icon={Clock}
      />
      {logistics.appointment_time_format === "arrival_window" && (
        <FormInput
          label="Window Size (hours)"
          sublabel="e.g., 2 for a 2-hour arrival window."
          type="number"
          min={1}
          value={logistics.window_size_hours || ""}
          onChange={(e) =>
            updateLogistics(
              "window_size_hours",
              e.target.value ? parseInt(e.target.value, 10) : 0
            )
          }
          icon={Clock}
        />
      )}

      <SubHeading title="5.7 Daily Limit" />
      <FormInput
        label="Max Appointments per Day"
        sublabel="Prevents the calendar from getting overloaded."
        type="number"
        min={0}
        value={logistics.max_appointments_per_day || ""}
        onChange={(e) =>
          updateLogistics(
            "max_appointments_per_day",
            e.target.value ? parseInt(e.target.value, 10) : 0
          )
        }
        icon={Target}
      />

      <SubHeading title="5.8 Custom Rules" />
      <StringListEditor
        label="Custom Schedule Rules"
        sublabel="Special conditions (e.g., 'No estimates on Fridays')."
        values={oh.custom_schedule_rules || []}
        onChange={(next) => updateOH("custom_schedule_rules", next)}
        placeholder="e.g., No estimates on Fridays"
        icon={FileText}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 6: Crew & Operations Schedule
// ------------------------------------------------------------
const Step6Operations = ({ formData, setFormData }) => {
  const ops = formData.knowledge_base.operations || {};

  const updateOps = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        operations: { ...ops, [field]: value },
      },
    });
  const updateFCH = (field, value) =>
    updateOps("field_crew_hours", { ...(ops.field_crew_hours || {}), [field]: value });
  const updateOH = (field, value) =>
    updateOps("office_hours", { ...(ops.office_hours || {}), [field]: value });
  const updateCap = (field, value) =>
    updateOps("crew_capacity", { ...(ops.crew_capacity || {}), [field]: value });
  const updateQW = (field, value) =>
    updateOps("quoting_workflow", { ...(ops.quoting_workflow || {}), [field]: value });

  const specs = ops.crew_capacity?.specializations || [];
  const setSpecs = (next) => updateCap("specializations", next);

  return (
    <div className="space-y-6" data-testid="roofing-step-6">
      <SectionHeading
        title="Crew & Operations Schedule"
        description="Internal schedules and crew capabilities — what your team can handle and when."
      />

      <SubHeading title="6.1 Trades Working Hours" />
      <div className="space-y-2">
        <DayWindowRow
          label="Mon – Fri"
          dayValue={ops.field_crew_hours?.monday_to_friday}
          onChange={(v) => updateFCH("monday_to_friday", v)}
          showIsOpen={false}
        />
        <DayWindowRow
          label="Saturday"
          dayValue={ops.field_crew_hours?.saturday}
          onChange={(v) => updateFCH("saturday", v)}
          showIsOpen={false}
        />
        <DayWindowRow
          label="Sunday"
          dayValue={ops.field_crew_hours?.sunday}
          onChange={(v) => updateFCH("sunday", v)}
          showIsOpen={false}
        />
      </div>

      <SubHeading title="6.2 Capacity" />
      <FormInput
        label="Concurrent Crews"
        sublabel="How many crews can work simultaneously?"
        type="number"
        min={0}
        value={ops.crew_capacity?.concurrent_crews || ""}
        onChange={(e) =>
          updateCap(
            "concurrent_crews",
            e.target.value ? parseInt(e.target.value, 10) : 0
          )
        }
        icon={Users}
      />

      <HiddenUi hidden={ROOFING_UI_HIDDEN.specialization_6_3}>
        <SubHeading title="6.3 Specialization" />
        <p className="text-[#9CA3AF] text-xs font-body">
          Configure each crew and the service types they focus on.
        </p>
        <div className="space-y-3">
          {specs.map((sp, idx) => (
            <div key={idx} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[#00D1FF] text-xs">Crew {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setSpecs(specs.filter((_, i) => i !== idx))}
                  className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={sp.crew_name || ""}
                onChange={(e) =>
                  setSpecs(
                    specs.map((it, i) => (i === idx ? { ...it, crew_name: e.target.value } : it))
                  )
                }
                placeholder="Crew Name (e.g., Crew Alpha)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
              <input
                type="text"
                value={(sp.focus || []).join(", ")}
                onChange={(e) =>
                  setSpecs(
                    specs.map((it, i) =>
                      i === idx
                        ? {
                            ...it,
                            focus: e.target.value
                              .split(",")
                              .map((x) => x.trim())
                              .filter(Boolean),
                          }
                        : it
                    )
                  )
                }
                placeholder="Focus areas, comma-separated (e.g., metal_roof_replacement, new_build_roofing)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSpecs([...(specs || []), { crew_name: "", focus: [] }])}
          className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
        >
          <Plus className="w-4 h-4" />
          Add Crew
        </button>
      </HiddenUi>

      <SubHeading title="6.4 Estimator Role" />
      <FormInput
        label="On-site Quoter / Estimator Role"
        type="text"
        placeholder="e.g., Dedicated Estimator"
        value={ops.quoting_workflow?.on_site_quoter_role || ""}
        onChange={(e) => updateQW("on_site_quoter_role", e.target.value)}
        icon={HardHat}
      />
      <RadioGroup
        label="Follow-up Booking Flow"
        options={[
          {
            value: "on_site_booking",
            label: "Estimator books on-site",
            description: "The estimator can commit a job date during the visit.",
          },
          {
            value: "office_routing",
            label: "Office manager books afterwards",
            description: "Quotes go back to the office for scheduling.",
          },
        ]}
        value={ops.quoting_workflow?.follow_up_booking_flow || "office_routing"}
        onChange={(v) => updateQW("follow_up_booking_flow", v)}
        icon={Briefcase}
      />
      <HiddenUi hidden={ROOFING_UI_HIDDEN.quotingWorkflowDescription_6_4}>
        <FormTextarea
          label="Quoting Workflow Description"
          rows={2}
          placeholder="The estimator provides the quote on-site, but dates are scheduled by the office afterwards."
          value={ops.quoting_workflow?.description || ""}
          onChange={(e) => updateQW("description", e.target.value)}
          icon={FileText}
        />
      </HiddenUi>

      <SubHeading title="6.5 Office Hours" />
      <div className="space-y-2">
        <DayWindowRow
          label="Mon – Fri"
          dayValue={ops.office_hours?.monday_to_friday}
          onChange={(v) => updateOH("monday_to_friday", v)}
          showIsOpen={false}
        />
        <DayWindowRow
          label="Saturday"
          dayValue={ops.office_hours?.saturday}
          onChange={(v) => updateOH("saturday", v)}
          showIsOpen={false}
        />
        <DayWindowRow
          label="Sunday"
          dayValue={ops.office_hours?.sunday}
          onChange={(v) => updateOH("sunday", v)}
          showIsOpen={false}
        />
      </div>
      <HiddenUi hidden={ROOFING_UI_HIDDEN.outOfHoursMessage_6_5}>
        <FormTextarea
          label="Out-of-Hours Message"
          sublabel="Shown when customers contact the office outside hours."
          rows={2}
          placeholder="Our office is currently closed..."
          value={ops.office_hours?.out_of_hours_message || ""}
          onChange={(e) => updateOH("out_of_hours_message", e.target.value)}
          icon={MessageSquare}
        />
      </HiddenUi>
    </div>
  );
};

// ------------------------------------------------------------
// Step 7: Lead Qualification — Questions & Logic
// ------------------------------------------------------------
const Step7Qualification = ({ formData, setFormData }) => {
  const ql = formData.qualification_logic || {};
  const questions = ql.questions || [];

  const update = (field, value) =>
    setFormData({
      ...formData,
      qualification_logic: { ...ql, [field]: value },
    });

  const setQuestions = (next) => update("questions", next);
  const updateField = (idx, field, value) =>
    setQuestions(questions.map((q, i) => (i === idx ? { ...q, [field]: value } : q)));

  const addQuestion = () =>
    setQuestions([
      ...questions,
      {
        question: "",
        key: "",
        internal_goal: "",
        turn_on: true,
        optional: false,
      },
    ]);

  return (
    <div className="space-y-6" data-testid="roofing-step-7">
      <SectionHeading
        title="Lead Qualification — Questions & Logic"
        description="Configure your Digital Brain during lead qualification."
      />

      <HiddenUi hidden={ROOFING_UI_HIDDEN.globalObjective_7_1}>
        <SubHeading title="7.1 Global Objective" />
        <FormTextarea
          label="Internal Goal Summary"
          sublabel="What should the AI prioritize during qualification?"
          rows={4}
          placeholder="Qualify the lead's urgency, gather exact address, identify property type..."
          value={ql.internal_goal_summary || ""}
          onChange={(e) => update("internal_goal_summary", e.target.value)}
          icon={Target}
        />
      </HiddenUi>

      <HiddenUi hidden={ROOFING_UI_HIDDEN.knownUserHandling_7_2}>
        <SubHeading title="7.2 Known User Handling" />
        <ToggleSwitch
          label="Skip qualification for known users"
          description="If the Digital Brain recognizes an existing customer, skip repeat questions."
          checked={!!ql.skip_qualification_if_known_user}
          onChange={(v) => update("skip_qualification_if_known_user", v)}
          icon={User}
        />
        <ToggleSwitch
          label="Strict qualification gate"
          description="Block scheduling until all required questions are answered."
          checked={!!ql.strict_qualification_gate}
          onChange={(v) => update("strict_qualification_gate", v)}
          icon={ShieldCheck}
        />
      </HiddenUi>

      <SubHeading title="7.3 Questionnaire" />
      <div className="space-y-4">
        {questions.map((q, idx) => (
          <div
            key={idx}
            className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3"
          >
            <div className="flex items-center justify-between flex-wrap gap-3">
              <span className="font-mono text-[#00D1FF] text-xs">Question {idx + 1}</span>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <span className="text-xs font-body text-[#9CA3AF]">Active</span>
                  <button
                    type="button"
                    onClick={() => updateField(idx, "turn_on", !q.turn_on)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      q.turn_on ? "bg-[#00D1FF]" : "bg-white/20"
                    }`}
                    aria-pressed={!!q.turn_on}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        q.turn_on ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-xs font-body text-[#9CA3AF]">Optional</span>
                  <button
                    type="button"
                    onClick={() => updateField(idx, "optional", !q.optional)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      q.optional ? "bg-amber-400" : "bg-white/20"
                    }`}
                    aria-pressed={!!q.optional}
                  >
                    <div
                      className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                        q.optional ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </label>
                {questions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setQuestions(questions.filter((_, i) => i !== idx))}
                    className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            <textarea
              value={q.question || ""}
              onChange={(e) => updateField(idx, "question", e.target.value)}
              rows={2}
              placeholder="Question the Digital Brain asks..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body resize-y"
            />
            <input
              type="text"
              value={q.key || ""}
              onChange={(e) => updateField(idx, "key", e.target.value)}
              placeholder="Data key (e.g., property_address)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
            />
            <input
              type="text"
              value={q.internal_goal || ""}
              onChange={(e) => updateField(idx, "internal_goal", e.target.value)}
              placeholder="Internal goal (e.g., Service area check)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={addQuestion}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Question
      </button>
    </div>
  );
};

// ------------------------------------------------------------
// Step 8: Disqualifiers — Auto-Decline Rules
// ------------------------------------------------------------
const PROPERTY_TYPE_OPTIONS = [
  { v: "commercial", label: "Commercial" },
  { v: "strata", label: "Strata / Body Corporate" },
  { v: "townhouse", label: "Townhouse" },
  { v: "unit", label: "Unit / Apartment" },
  { v: "industrial", label: "Industrial" },
  { v: "heritage", label: "Heritage Listed" },
];

const TENANT_POLICY_OPTIONS = [
  { value: "accept", label: "Accept tenant requests" },
  { value: "request_owner_details", label: "Request owner authorization first" },
  { value: "decline", label: "Decline tenant requests" },
];

const ASBESTOS_POLICY_OPTIONS = [
  { value: "accept", label: "We handle asbestos removal" },
  { value: "refer_to_partner", label: "Refer to a licensed partner" },
  { value: "decline", label: "Politely decline" },
];

const Step8Disqualifiers = ({ formData, setFormData }) => {
  const ql = formData.qualification_logic || {};
  const dr = ql.disqualification_rules || {};

  const updateDR = (field, value) =>
    setFormData({
      ...formData,
      qualification_logic: {
        ...ql,
        disqualification_rules: { ...dr, [field]: value },
      },
    });
  const updateRej = (field, value) =>
    updateDR("rejection_messages", { ...(dr.rejection_messages || {}), [field]: value });

  const toggleBlocked = (val) => {
    const list = dr.blocked_property_types || [];
    const next = list.includes(val) ? list.filter((x) => x !== val) : [...list, val];
    updateDR("blocked_property_types", next);
  };

  return (
    <div className="space-y-6" data-testid="roofing-step-8">
      <SectionHeading
        title="Disqualifiers — Auto-Decline Rules"
        description="Rules for politely declining leads that don't fit your business."
      />

      <SubHeading title="8.1 Disqualification Limits" />

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body">
          <AlertTriangle className="w-4 h-4 text-[#00D1FF]" />
          Blocked Property Types
        </label>
        <p className="text-[#9CA3AF] text-xs font-body">Select the types you do NOT accept.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {PROPERTY_TYPE_OPTIONS.map((pt) => {
            const active = (dr.blocked_property_types || []).includes(pt.v);
            return (
              <button
                key={pt.v}
                type="button"
                onClick={() => toggleBlocked(pt.v)}
                className={`px-3 py-2 rounded-lg text-sm border transition-all font-body ${
                  active
                    ? "bg-red-500/15 border-red-400/50 text-red-300"
                    : "bg-white/5 border-white/10 text-[#9CA3AF] hover:border-white/20"
                }`}
              >
                {pt.label}
              </button>
            );
          })}
        </div>
      </div>

      <FormInput
        label="Maximum Storeys"
        sublabel="Buildings with more storeys than this are auto-declined."
        type="number"
        min={1}
        max={20}
        value={dr.max_storeys || ""}
        onChange={(e) =>
          updateDR("max_storeys", e.target.value ? parseInt(e.target.value, 10) : 0)
        }
        icon={Building2}
      />

      <RadioGroup
        label="Tenant Policy"
        options={TENANT_POLICY_OPTIONS}
        value={dr.tenant_policy || "request_owner_details"}
        onChange={(v) => updateDR("tenant_policy", v)}
        icon={Users}
      />

      <RadioGroup
        label="Asbestos Policy"
        options={ASBESTOS_POLICY_OPTIONS}
        value={dr.asbestos_policy || "refer_to_partner"}
        onChange={(v) => updateDR("asbestos_policy", v)}
        icon={AlertTriangle}
      />

      <SubHeading title="8.2 Custom Restrictions" />
      <StringListEditor
        label="Custom Auto-Declines"
        sublabel="e.g., We don't work on thatched roofs"
        values={dr.custom_auto_declines || []}
        onChange={(next) => updateDR("custom_auto_declines", next)}
        placeholder="e.g., Thatch roofs"
        icon={AlertTriangle}
      />

      <SubHeading title="8.3 Action on Disqualification" />
      <RadioGroup
        label="What should the Digital Brain do with a disqualified lead?"
        options={[
          { value: "decline", label: "Politely decline" },
          { value: "manager", label: "Escalate to Manager" },
          { value: "website", label: "Redirect to website" },
        ]}
        value={ql.unqualified_action || "manager"}
        onChange={(v) =>
          setFormData({
            ...formData,
            qualification_logic: { ...ql, unqualified_action: v },
          })
        }
        icon={MessageSquare}
      />

      <SubHeading title="8.4 Rejection Scripts" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Predefined polite responses for each rejection reason.
      </p>

      {[
        { key: "out_of_area", label: "Out of service area" },
        { key: "service_not_offered", label: "Service not offered" },
        { key: "property_type", label: "Property type not accepted" },
        { key: "tenant_no_authority", label: "Tenant without authority" },
        { key: "asbestos_referral", label: "Asbestos referral" },
        { key: "height_limit", label: "Exceeds height limit" },
        { key: "default_polite_no", label: "Default polite no" },
      ].map((item) => (
        <FormTextarea
          key={item.key}
          label={item.label}
          rows={2}
          value={dr.rejection_messages?.[item.key] || ""}
          onChange={(e) => updateRej(item.key, e.target.value)}
          placeholder={`Message shown when: ${item.label}`}
          icon={MessageSquare}
        />
      ))}
    </div>
  );
};

// ------------------------------------------------------------
// Step 9: Pricing & Quote Policy
// ------------------------------------------------------------
const Step9Pricing = ({ formData, setFormData }) => {
  const pp = formData.knowledge_base.pricing_policy || {};

  const updatePP = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        pricing_policy: { ...pp, [field]: value },
      },
    });
  const updateQR = (field, value) =>
    updatePP("quote_rules", { ...(pp.quote_rules || {}), [field]: value });
  const updateJR = (field, value) =>
    updatePP("job_requirements", { ...(pp.job_requirements || {}), [field]: value });
  const updatePF = (field, value) =>
    updatePP("payment_and_finance", { ...(pp.payment_and_finance || {}), [field]: value });
  const updateFin = (field, value) =>
    updatePF("financing_options", { ...(pp.payment_and_finance?.financing_options || {}), [field]: value });
  const updateBP = (field, value) =>
    updatePP("ballpark_pricing", { ...(pp.ballpark_pricing || {}), [field]: value });

  const products = pp.ballpark_pricing?.products || [];
  const setProducts = (next) => updateBP("products", next);

  const paymentMethods = pp.payment_and_finance?.payment_methods || [];

  return (
    <div className="space-y-6" data-testid="roofing-step-9">
      <SectionHeading
        title="Pricing & Quote Policy"
        description="How the Digital Brain discusses pricing, fees and payment with customers."
      />

      <SubHeading title="9.1 Free Estimate Standard" />
      <ToggleSwitch
        label="Free estimates are standard"
        description="Offered across all services by default."
        checked={!!pp.quote_rules?.free_quotes_standard}
        onChange={(v) => updateQR("free_quotes_standard", v)}
        icon={DollarSign}
      />

      <SubHeading title="9.3 Job Requirements" />
      <FormInput
        label="Minimum Job Value"
        type="text"
        placeholder="e.g., $500"
        value={pp.job_requirements?.minimum_job_value || ""}
        onChange={(e) => updateJR("minimum_job_value", e.target.value)}
        icon={DollarSign}
      />
      <FormInput
        label="Deposit Required"
        type="text"
        placeholder="e.g., 20% upon acceptance"
        value={pp.job_requirements?.deposit_required || ""}
        onChange={(e) => updateJR("deposit_required", e.target.value)}
        icon={DollarSign}
      />

      <SubHeading title="9.4 Taxes & Payments" />
      <RadioGroup
        label="Tax Status"
        options={[
          { value: "GST inclusive", label: "GST inclusive" },
          { value: "GST exclusive", label: "GST exclusive" },
          { value: "Tax-exempt", label: "Tax-exempt" },
        ]}
        value={pp.payment_and_finance?.tax_status || "GST inclusive"}
        onChange={(v) => updatePF("tax_status", v)}
        icon={FileText}
      />
      <StringListEditor
        label="Payment Methods"
        sublabel="Accepted forms of payment."
        values={paymentMethods}
        onChange={(next) => updatePF("payment_methods", next)}
        placeholder="e.g., Bank Transfer"
        icon={DollarSign}
      />

      <HiddenUi hidden={ROOFING_UI_HIDDEN.financing_9_5}>
        <SubHeading title="9.5 Financing" />
        <ToggleSwitch
          label="Financing / payment plans offered"
          checked={!!pp.payment_and_finance?.financing_options?.offered}
          onChange={(v) => updateFin("offered", v)}
          icon={Scale}
        />
        {pp.payment_and_finance?.financing_options?.offered && (
          <FormInput
            label="Finance Provider Name"
            type="text"
            placeholder="e.g., Brighte Finance"
            value={pp.payment_and_finance?.financing_options?.provider_name || ""}
            onChange={(e) => updateFin("provider_name", e.target.value)}
            icon={Briefcase}
          />
        )}
      </HiddenUi>

      <HiddenUi hidden={ROOFING_UI_HIDDEN.ballparkPricing_9_6}>
        <SubHeading title="9.6 Ballpark Pricing" />
        <ToggleSwitch
          label="Enforce 'No Exact Prices' policy"
          description="Never quote exact dollar amounts — always redirect to an on-site inspection."
          checked={!!pp.ballpark_pricing?.enforce_no_exact_prices}
          onChange={(v) => updateBP("enforce_no_exact_prices", v)}
          icon={ShieldCheck}
        />
        <p className="text-[#9CA3AF] text-xs font-body mt-2">
          Define price ranges per material / product. The Digital Brain will share these ranges only.
        </p>
        <div className="space-y-3">
          {products.map((p, idx) => (
            <div key={idx} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[#00D1FF] text-xs">Product {idx + 1}</span>
                <button
                  type="button"
                  onClick={() => setProducts(products.filter((_, i) => i !== idx))}
                  className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={p.product_name || ""}
                onChange={(e) =>
                  setProducts(
                    products.map((it, i) =>
                      i === idx ? { ...it, product_name: e.target.value } : it
                    )
                  )
                }
                placeholder="Product name (e.g., asphalt_shingles)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
              <input
                type="text"
                value={p.pricing || ""}
                onChange={(e) =>
                  setProducts(
                    products.map((it, i) => (i === idx ? { ...it, pricing: e.target.value } : it))
                  )
                }
                placeholder="Pricing (e.g., $400 - $600 per square)"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
              />
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setProducts([...products, { product_name: "", pricing: "" }])}
          className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
        >
          <Plus className="w-4 h-4" />
          Add Product
        </button>
      </HiddenUi>
    </div>
  );
};

// ------------------------------------------------------------
// Step 10 (section 11): Brand Voice & Communication
// ------------------------------------------------------------
const Step10Tone = ({ formData, setFormData, errors }) => {
  const tov = formData.tone_of_voice || {};
  const update = (field, value) =>
    setFormData({ ...formData, tone_of_voice: { ...tov, [field]: value } });

  return (
    <div className="space-y-6" data-testid="roofing-step-10">
      <SectionHeading
        title="Brand Voice & Communication"
        description="The Digital Brain's personality and how it represents your brand."
      />

      <SubHeading title="11.1 Tone & Style" />
      <FormInput
        label="Digital Brain Name"
        type="text"
        placeholder="Jack"
        value={tov.bot_name || ""}
        onChange={(e) => update("bot_name", e.target.value)}
        icon={User}
      />
      <RadioGroup
        label="Communication Style"
        required
        options={[
          {
            value: "blue_collar_professional",
            label: "Blue-collar professional",
            description: "Direct, practical, trustworthy.",
          },
          {
            value: "friendly_energetic",
            label: "Friendly & Energetic",
            description: "Warm, upbeat, approachable.",
          },
          {
            value: "professional_corporate",
            label: "Professional & Corporate",
            description: "Polished, formal, business-first.",
          },
        ]}
        value={tov.style}
        onChange={(v) => update("style", v)}
        error={errors?.style}
        icon={Palette}
      />
      <ToggleSwitch
        label="Use emojis in messages"
        checked={!!tov.use_emojis}
        onChange={(v) => update("use_emojis", v)}
        icon={Smile}
      />
      <ToggleSwitch
        label={`Use Aussie slang (e.g., "G'day, mate")`}
        checked={!!tov.use_aussie_slang}
        onChange={(v) => update("use_aussie_slang", v)}
        icon={MessageSquare}
      />

      <SubHeading title="11.2 AI Disclosure" />
      <ToggleSwitch
        label="Disclose AI status"
        description="Whether the Digital Brain should admit it is an AI when asked."
        checked={!!tov.disclose_ai_status}
        onChange={(v) => update("disclose_ai_status", v)}
        icon={Info}
      />

      <SubHeading title="11.3 Filters" />
      <StringListEditor
        label="Forbidden Phrases"
        sublabel="Legal protection — the Digital Brain will never say these."
        values={tov.forbidden_phrases || []}
        onChange={(next) => update("forbidden_phrases", next)}
        placeholder="e.g., guarantee"
        icon={Gavel}
      />
      <StringListEditor
        label="Preferred Phrases / Slogans"
        sublabel="Brand phrases the Digital Brain should weave into replies."
        values={tov.preferred_phrases || []}
        onChange={(next) => update("preferred_phrases", next)}
        placeholder="e.g., We've got your roof covered"
        icon={Sparkles}
      />
      <FormInput
        label="Tagline"
        type="text"
        placeholder="e.g., Built to withstand anything"
        value={tov.tagline || ""}
        onChange={(e) => update("tagline", e.target.value)}
        icon={Sparkles}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 11 (section 13): Lead Routing & Escalation
// ------------------------------------------------------------
const SCENARIO_META = {
  Emergency: {
    title: "Scenario A — EMERGENCY",
    hint: "Active leaks, collapsed roof, storm damage requiring immediate response.",
    icon: Siren,
    showMinProjectSize: false,
    showEmail: true,
    showPhone: true,
  },
  warranty: {
    title: "Scenario B — WARRANTY",
    hint: "Complaints or issues with past work; post-install callbacks.",
    icon: Wrench,
    showMinProjectSize: false,
    showEmail: true,
    showPhone: true,
  },
  commercial: {
    title: "Scenario C — COMMERCIAL",
    hint: "Large-scale commercial / multi-building projects.",
    icon: Building2,
    showMinProjectSize: true,
    showEmail: true,
    showPhone: true,
  },
  general: {
    title: "Scenario D — GENERAL SUPPORT",
    hint: "Aggressive users, explicit human requests, or fallbacks.",
    icon: User,
    showMinProjectSize: false,
    showEmail: true,
    showPhone: true,
  },
};

const Step11Handoff = ({ formData, setFormData }) => {
  const hs = formData.knowledge_base.handoff_settings || {};
  const roles = hs.roles || [];

  const updateHS = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        handoff_settings: { ...hs, [field]: value },
      },
    });
  const updateRole = (idx, field, value) =>
    updateHS(
      "roles",
      roles.map((r, i) => (i === idx ? { ...r, [field]: value } : r))
    );
  const updateHM = (field, value) =>
    updateHS("handoff_messages", { ...(hs.handoff_messages || {}), [field]: value });

  return (
    <div className="space-y-6" data-testid="roofing-step-11">
      <SectionHeading
        title="Lead Routing & Escalation"
        description="Who the Digital Brain hands off to when situations get complex."
      />

      <HiddenUi hidden={ROOFING_UI_HIDDEN.supportRoles_13_1}>
        <SubHeading title="13.1 Support Roles" />
        <div className="space-y-5">
          {roles.map((role, idx) => {
            const meta = SCENARIO_META[role.type] || {
              title: role.type,
              hint: "",
              icon: User,
              showEmail: true,
              showPhone: true,
              showMinProjectSize: false,
            };
            const SIcon = meta.icon;
            return (
              <div
                key={role.type || idx}
                className="p-5 rounded-xl bg-white/5 border border-white/10 space-y-4"
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#00D1FF]/15 flex items-center justify-center flex-shrink-0">
                    <SIcon className="w-5 h-5 text-[#00D1FF]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-[#F3F4F6] text-sm font-heading font-bold">
                      {meta.title}
                    </h3>
                    {meta.hint && (
                      <p className="text-[#9CA3AF] text-xs font-body mt-0.5">{meta.hint}</p>
                    )}
                  </div>
                </div>

                <FormInput
                  label="Role Title"
                  type="text"
                  placeholder="e.g., On-call Technician"
                  value={role.role || ""}
                  onChange={(e) => updateRole(idx, "role", e.target.value)}
                  icon={Briefcase}
                />
                <FormInput
                  label="Person Name"
                  type="text"
                  placeholder="e.g., Marcus Thorp"
                  value={role.name || ""}
                  onChange={(e) => updateRole(idx, "name", e.target.value)}
                  icon={User}
                />
                {meta.showPhone && (
                  <FormInput
                    label="Phone Number"
                    type="tel"
                    placeholder="e.g., 0481-612-000"
                    value={role.phone || ""}
                    onChange={(e) => updateRole(idx, "phone", e.target.value)}
                    icon={Phone}
                  />
                )}
                {meta.showEmail && (
                  <FormInput
                    label="Email"
                    type="email"
                    placeholder="e.g., warranty@company.com"
                    value={role.email || ""}
                    onChange={(e) => updateRole(idx, "email", e.target.value)}
                    icon={Mail}
                  />
                )}
                {meta.showMinProjectSize && (
                  <FormInput
                    label="Minimum Project Size"
                    type="text"
                    placeholder="e.g., 10,000 sq ft"
                    value={role.min_project_size || ""}
                    onChange={(e) => updateRole(idx, "min_project_size", e.target.value)}
                    icon={Building2}
                  />
                )}
                <FormInput
                  label="Availability Hours"
                  type="text"
                  placeholder="e.g., 24/7"
                  value={role.availability_hours || ""}
                  onChange={(e) => updateRole(idx, "availability_hours", e.target.value)}
                  icon={Clock}
                />
                <AllowedChannelsPicker
                  value={role.allowed_channels || []}
                  onChange={(next) => updateRole(idx, "allowed_channels", next)}
                />
              </div>
            );
          })}
        </div>
      </HiddenUi>

      <SubHeading title="13.2 Handoff Triggers" />
      <StringListEditor
        label="When should the Digital Brain escalate to a human?"
        sublabel="e.g., abuse, repeated manager requests, complex insurance claims."
        values={hs.handoff_triggers || []}
        onChange={(next) => updateHS("handoff_triggers", next)}
        placeholder="e.g., customer_complaint_or_angry"
        icon={AlertTriangle}
      />

      <SubHeading title="13.3 Handoff Messages" />
      <FormTextarea
        label="General Escalation Message"
        rows={2}
        placeholder="I'll have one of our human specialists take over..."
        value={hs.handoff_messages?.general_escalation || ""}
        onChange={(e) => updateHM("general_escalation", e.target.value)}
        icon={MessageSquare}
      />
      <FormTextarea
        label="Emergency Escalation Message"
        rows={2}
        placeholder="I'm routing you directly to our on-call technician..."
        value={hs.handoff_messages?.emergency_escalation || ""}
        onChange={(e) => updateHM("emergency_escalation", e.target.value)}
        icon={Siren}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 12 (section 16): Legal & Compliance
// ------------------------------------------------------------
const Step12Legal = ({ formData, setFormData }) => {
  const lc = formData.knowledge_base.legal_and_compliance || {};
  const update = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...formData.knowledge_base,
        legal_and_compliance: { ...lc, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="roofing-step-12">
      <SectionHeading
        title="Legal & Compliance"
        description="Legal references and regulatory obligations the Digital Brain must consider."
      />

      <SubHeading title="16.1 Privacy" />
      <FormInput
        label="Privacy Policy URL"
        type="url"
        placeholder="https://your-company.com/privacy"
        value={lc.privacy_policy_url || ""}
        onChange={(e) => update("privacy_policy_url", e.target.value)}
        icon={Globe}
      />

      <SubHeading title="16.2 State Regulations" />
      <FormTextarea
        label="State / Regional Regulations"
        sublabel="Legal obligations the Digital Brain must remember (cooling-off periods, licensing, etc.)"
        rows={4}
        placeholder="e.g., By state law, jobs over $5,000 include a 5-day cooling-off period."
        value={lc.state_regulations || ""}
        onChange={(e) => update("state_regulations", e.target.value)}
        icon={Gavel}
      />
    </div>
  );
};

// ------------------------------------------------------------
// Step 13 (section 18): Competitive Strategy & Success
// ------------------------------------------------------------
const Step13Strategy = ({ formData, setFormData }) => {
  const kb = formData.knowledge_base;
  const objections = kb.objection_handling || [];
  const cs = kb.competitive_strategy || {};

  const setObjections = (next) =>
    setFormData({
      ...formData,
      knowledge_base: { ...kb, objection_handling: next },
    });
  const updateCS = (field, value) =>
    setFormData({
      ...formData,
      knowledge_base: {
        ...kb,
        competitive_strategy: { ...cs, [field]: value },
      },
    });

  return (
    <div className="space-y-6" data-testid="roofing-step-13">
      <SectionHeading
        title="Competitive Strategy & Success"
        description="How the Digital Brain positions you against competitors and handles objections."
      />

      <SubHeading title="18.1 Objection Handling" />
      <p className="text-[#9CA3AF] text-xs font-body">
        Cheat sheet for common customer concerns — the Digital Brain will reach for these replies.
      </p>
      <div className="space-y-3">
        {objections.map((o, idx) => (
          <div key={idx} className="p-4 rounded-lg bg-white/5 border border-white/10 space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[#00D1FF] text-xs">Objection {idx + 1}</span>
              <button
                type="button"
                onClick={() => setObjections(objections.filter((_, i) => i !== idx))}
                className="text-[#9CA3AF] hover:text-red-400 transition-colors p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <input
              type="text"
              value={o.objection || ""}
              onChange={(e) =>
                setObjections(
                  objections.map((it, i) => (i === idx ? { ...it, objection: e.target.value } : it))
                )
              }
              placeholder="Customer concern (e.g., Price is too high / I found it cheaper)"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body"
            />
            <textarea
              value={o.response || ""}
              onChange={(e) =>
                setObjections(
                  objections.map((it, i) => (i === idx ? { ...it, response: e.target.value } : it))
                )
              }
              rows={3}
              placeholder="Recommended reply..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#00D1FF]/50 text-sm font-body resize-y"
            />
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => setObjections([...objections, { objection: "", response: "" }])}
        className="flex items-center gap-2 text-[#00D1FF] text-sm font-body hover:text-[#00D1FF]/80 transition-colors py-2"
      >
        <Plus className="w-4 h-4" />
        Add Objection
      </button>

      <SubHeading title="18.2 Strategy" />
      <StringListEditor
        label="Competitors Often Mentioned"
        sublabel="Names customers sometimes compare you to."
        values={cs.competitors_often_mentioned || []}
        onChange={(next) => updateCS("competitors_often_mentioned", next)}
        placeholder="e.g., Local Roofers Co"
        icon={Target}
      />
      <FormTextarea
        label="Core Differentiator"
        sublabel="Your key advantage the Digital Brain should emphasize."
        rows={3}
        placeholder="We provide a 10-year leak-free guarantee..."
        value={cs.core_differentiator || ""}
        onChange={(e) => updateCS("core_differentiator", e.target.value)}
        icon={Sparkles}
      />
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

// ==================== BACKEND LOAD ADAPTER ====================
// Backward-compat helper: legacy `services_offered` payloads saved via the
// old `/save_settings` endpoint are stored as an array of {title, description}
// records. This converts them back into the tri-state dict the form expects
// so existing users don't lose their UI state on first load after the
// endpoint migration.

const servicesListToDict = (list, defaults) => {
  if (!Array.isArray(list)) return defaults;
  const out = { ...defaults };
  list.forEach((item) => {
    if (!item || !item.title) return;
    if (item.title === "other_services") {
      const raw = item.description || "";
      const sep = raw.indexOf("::");
      if (sep >= 0) {
        out.other_services = {
          status: raw.slice(0, sep) || "false",
          description: raw.slice(sep + 2),
        };
      } else {
        out.other_services = {
          status: raw || "false",
          description: "",
        };
      }
    } else {
      out[item.title] = item.description || "false";
    }
  });
  return out;
};

const RoofingBotSettings = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const steps = [
    { shortTitle: "Company" },
    { shortTitle: "Area" },
    { shortTitle: "Services" },
    { shortTitle: "Appts" },
    { shortTitle: "Calendar" },
    { shortTitle: "Crew" },
    { shortTitle: "Qualify" },
    { shortTitle: "Decline" },
    { shortTitle: "Pricing" },
    { shortTitle: "Voice" },
    { shortTitle: "Handoff" },
    { shortTitle: "Legal" },
    { shortTitle: "Strategy" },
  ];
  const totalSteps = steps.length;

  // ---- Load existing settings ----
  useEffect(() => {
    const loadExisting = async () => {
      const token = getClientAuthToken();
    if (!token) {
        setIsLoading(false);
        return;
      }
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const res = await axios.get(`${backendUrl}/api/bot/settings`, {
          headers: getClientAuthHeaders(),
        });
        if (res.data?.has_settings && res.data.settings) {
          const s = res.data.settings;
          const isRoofing = s.bot_type === "roofing_v1";
          if (isRoofing) {
            setHasExistingSettings(true);
            // Deep-merge with defaults to tolerate partial saves from older versions
            setFormData({
              knowledge_base: {
                ...initialFormData.knowledge_base,
                ...(s.knowledge_base || {}),
                credentials: {
                  ...initialFormData.knowledge_base.credentials,
                  ...(s.knowledge_base?.credentials || {}),
                },
                office_location: {
                  ...initialFormData.knowledge_base.office_location,
                  ...(s.knowledge_base?.office_location || {}),
                },
                service_area: {
                  ...initialFormData.knowledge_base.service_area,
                  ...(s.knowledge_base?.service_area || {}),
                  regional_callouts: {
                    ...initialFormData.knowledge_base.service_area.regional_callouts,
                    ...(s.knowledge_base?.service_area?.regional_callouts || {}),
                  },
                },
                legal_and_compliance: {
                  ...initialFormData.knowledge_base.legal_and_compliance,
                  ...(s.knowledge_base?.legal_and_compliance || {}),
                },
                operations: {
                  ...initialFormData.knowledge_base.operations,
                  ...(s.knowledge_base?.operations || {}),
                  office_hours: {
                    ...initialFormData.knowledge_base.operations.office_hours,
                    ...(s.knowledge_base?.operations?.office_hours || {}),
                  },
                  field_crew_hours: {
                    ...initialFormData.knowledge_base.operations.field_crew_hours,
                    ...(s.knowledge_base?.operations?.field_crew_hours || {}),
                  },
                  crew_capacity: {
                    ...initialFormData.knowledge_base.operations.crew_capacity,
                    ...(s.knowledge_base?.operations?.crew_capacity || {}),
                  },
                  quoting_workflow: {
                    ...initialFormData.knowledge_base.operations.quoting_workflow,
                    ...(s.knowledge_base?.operations?.quoting_workflow || {}),
                  },
                },
                handoff_settings: {
                  ...initialFormData.knowledge_base.handoff_settings,
                  ...(s.knowledge_base?.handoff_settings || {}),
                  roles:
                    Array.isArray(s.knowledge_base?.handoff_settings?.roles) &&
                    s.knowledge_base.handoff_settings.roles.length > 0
                      ? initialFormData.knowledge_base.handoff_settings.roles.map((def) => {
                          const match = s.knowledge_base.handoff_settings.roles.find(
                            (r) => r.type === def.type
                          );
                          return match ? { ...def, ...match } : def;
                        })
                      : initialFormData.knowledge_base.handoff_settings.roles,
                  handoff_messages: {
                    ...initialFormData.knowledge_base.handoff_settings.handoff_messages,
                    ...(s.knowledge_base?.handoff_settings?.handoff_messages || {}),
                  },
                },
                pricing_policy: {
                  ...initialFormData.knowledge_base.pricing_policy,
                  ...(s.knowledge_base?.pricing_policy || {}),
                  quote_rules: {
                    ...initialFormData.knowledge_base.pricing_policy.quote_rules,
                    ...(s.knowledge_base?.pricing_policy?.quote_rules || {}),
                  },
                  job_requirements: {
                    ...initialFormData.knowledge_base.pricing_policy.job_requirements,
                    ...(s.knowledge_base?.pricing_policy?.job_requirements || {}),
                  },
                  payment_and_finance: {
                    ...initialFormData.knowledge_base.pricing_policy.payment_and_finance,
                    ...(s.knowledge_base?.pricing_policy?.payment_and_finance || {}),
                    financing_options: {
                      ...initialFormData.knowledge_base.pricing_policy.payment_and_finance
                        .financing_options,
                      ...(s.knowledge_base?.pricing_policy?.payment_and_finance
                        ?.financing_options || {}),
                    },
                  },
                  ballpark_pricing: {
                    ...initialFormData.knowledge_base.pricing_policy.ballpark_pricing,
                    ...(s.knowledge_base?.pricing_policy?.ballpark_pricing || {}),
                  },
                },
                services_offered: Array.isArray(s.knowledge_base?.services_offered)
                  ? servicesListToDict(
                      s.knowledge_base.services_offered,
                      initialFormData.knowledge_base.services_offered
                    )
                  : {
                      ...initialFormData.knowledge_base.services_offered,
                      ...(s.knowledge_base?.services_offered || {}),
                      other_services: {
                        ...initialFormData.knowledge_base.services_offered.other_services,
                        ...(s.knowledge_base?.services_offered?.other_services || {}),
                      },
                    },
                objection_handling:
                  Array.isArray(s.knowledge_base?.objection_handling) &&
                  s.knowledge_base.objection_handling.length > 0
                    ? s.knowledge_base.objection_handling
                    : initialFormData.knowledge_base.objection_handling,
                competitive_strategy: {
                  ...initialFormData.knowledge_base.competitive_strategy,
                  ...(s.knowledge_base?.competitive_strategy || {}),
                },
                social_networks:
                  Array.isArray(s.knowledge_base?.social_networks) &&
                  s.knowledge_base.social_networks.length > 0
                    ? s.knowledge_base.social_networks
                    : initialFormData.knowledge_base.social_networks,
              },
              qualification_logic: {
                ...initialFormData.qualification_logic,
                ...(s.qualification_logic || {}),
                questions:
                  Array.isArray(s.qualification_logic?.questions) &&
                  s.qualification_logic.questions.length > 0
                    ? s.qualification_logic.questions
                    : initialFormData.qualification_logic.questions,
                disqualification_rules: {
                  ...initialFormData.qualification_logic.disqualification_rules,
                  ...(s.qualification_logic?.disqualification_rules || {}),
                  // Prefer the hoisted root-level list if the nested one is empty
                  out_of_scope_services:
                    (s.qualification_logic?.disqualification_rules?.out_of_scope_services || []).length > 0
                      ? s.qualification_logic.disqualification_rules.out_of_scope_services
                      : Array.isArray(s.knowledge_base?.out_of_scope_services) &&
                        s.knowledge_base.out_of_scope_services.length > 0
                      ? s.knowledge_base.out_of_scope_services
                      : initialFormData.qualification_logic.disqualification_rules
                          .out_of_scope_services,
                  rejection_messages: {
                    ...initialFormData.qualification_logic.disqualification_rules
                      .rejection_messages,
                    ...(s.qualification_logic?.disqualification_rules?.rejection_messages || {}),
                  },
                },
              },
              scheduling: {
                ...initialFormData.scheduling,
                ...(s.scheduling || {}),
                operating_hours: {
                  ...initialFormData.scheduling.operating_hours,
                  ...(s.scheduling?.operating_hours || {}),
                  public_holidays: {
                    ...initialFormData.scheduling.operating_hours.public_holidays,
                    ...(s.scheduling?.operating_hours?.public_holidays || {}),
                  },
                  lunch_break: {
                    ...initialFormData.scheduling.operating_hours.lunch_break,
                    ...(s.scheduling?.operating_hours?.lunch_break || {}),
                  },
                },
                logistics: {
                  ...initialFormData.scheduling.logistics,
                  ...(s.scheduling?.logistics || {}),
                },
                booking_rules: {
                  ...initialFormData.scheduling.booking_rules,
                  ...(s.scheduling?.booking_rules || {}),
                },
                booking_channel: {
                  ...initialFormData.scheduling.booking_channel,
                  ...(s.scheduling?.booking_channel || {}),
                },
                appointment_types: {
                  ...initialFormData.scheduling.appointment_types,
                  ...(s.scheduling?.appointment_types || {}),
                },
              },
              tone_of_voice: {
                ...initialFormData.tone_of_voice,
                ...(s.tone_of_voice || {}),
              },
            });
          }
        }
      } catch (err) {
        console.error("Error loading bot settings:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadExisting();
  }, []);

  // ---- LocalStorage: load on first render if no DB data ----
  useEffect(() => {
    if (!hasExistingSettings && !isLoading) {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setFormData((prev) => ({ ...prev, ...parsed }));
        } catch (e) {
          console.error("Error parsing saved roofing data:", e);
        }
      }
    }
  }, [hasExistingSettings, isLoading]);

  // ---- Autosave to LocalStorage ----
  const saveToLocalStorage = useCallback(() => {
    if (!hasExistingSettings || isEditMode) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
    }
  }, [formData, hasExistingSettings, isEditMode]);

  useEffect(() => {
    saveToLocalStorage();
  }, [saveToLocalStorage]);

  // ---- Validation per step ----
  const validateStep = (step) => {
    const newErrors = {};
    switch (step) {
      case 1:
        if (!formData.knowledge_base.company_name?.trim()) {
          newErrors.company_name = "Company name is required";
        }
        if (
          formData.knowledge_base.website_url &&
          !validateUrl(formData.knowledge_base.website_url)
        ) {
          newErrors.website_url = "Please enter a valid URL";
        }
        break;
      case 10:
        if (!formData.tone_of_voice.style) {
          newErrors.style = "Please select a communication style";
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildSubmissionPayload = () => ({
    bot_type: "roofing_v1",
    ...formData,
  });

  const saveSettingsToDb = async () => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const headers = { "Content-Type": "application/json", ...getClientAuthHeaders() };

    try {
      const res = await axios.post(
        `${backendUrl}/api/bot/save_settings/roofing_bot`,
        buildSubmissionPayload(),
        { headers }
      );

      if (res.data && res.data.status === "ok") {
        localStorage.removeItem(STORAGE_KEY);
        setHasExistingSettings(true);
        return true;
      }

      setErrors({
        submit: formatApiError(
          res.data?.detail ?? res.data?.message,
          "Failed to save settings. Please try again."
        ),
      });
      return false;
    } catch (err) {
      console.error("Save error:", err);
      setErrors({
        submit: formatApiError(
          err.response?.data?.detail ?? err.response?.data?.message,
          "Network error. Please check your connection and try again."
        ),
      });
      return false;
    }
  };

  const handleNext = async () => {
    if (!validateStep(currentStep)) return;
    if (currentStep >= totalSteps) return;

    setIsSaving(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.submit;
      return next;
    });

    const ok = await saveSettingsToDb();
    setIsSaving(false);

    if (!ok) return;

    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors({});
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Normalize various API error shapes (string, object, FastAPI 422 array) into a single string
  const formatApiError = (raw, fallback = "Failed to save settings. Please try again.") => {
    if (!raw) return fallback;
    if (typeof raw === "string") return raw;
    if (Array.isArray(raw)) {
      // FastAPI/Pydantic validation error array: [{ type, loc, msg, input, url }, ...]
      return (
        raw
          .map((e) => {
            if (typeof e === "string") return e;
            const path = Array.isArray(e?.loc)
              ? e.loc.filter((p) => p !== "body").join(".")
              : "";
            const message = e?.msg || e?.message || JSON.stringify(e);
            return path ? `${path}: ${message}` : message;
          })
          .join("; ") || fallback
      );
    }
    if (typeof raw === "object") {
      if (raw.msg) return raw.msg;
      if (raw.message) return raw.message;
      if (raw.detail) return formatApiError(raw.detail, fallback);
      try {
        return JSON.stringify(raw);
      } catch {
        return fallback;
      }
    }
    return String(raw);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) return;

    setIsSaving(true);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.submit;
      return next;
    });

    const ok = await saveSettingsToDb();
    setIsSaving(false);

    if (!ok) return;

    setIsSubmitted(true);
    setIsEditMode(false);
  };

  const handleEditSettings = () => {
    setIsEditMode(true);
    setIsSubmitted(false);
    setCurrentStep(1);
  };

  const isNextDisabled = () => {
    switch (currentStep) {
      case 1:
        return !formData.knowledge_base.company_name?.trim();
      case 10:
        return !formData.tone_of_voice.style;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Company formData={formData} setFormData={setFormData} errors={errors} />;
      case 2:
        return <Step2ServiceArea formData={formData} setFormData={setFormData} />;
      case 3:
        return <Step3Services formData={formData} setFormData={setFormData} />;
      case 4:
        return <Step4Appointments formData={formData} setFormData={setFormData} />;
      case 5:
        return <Step5Calendar formData={formData} setFormData={setFormData} />;
      case 6:
        return <Step6Operations formData={formData} setFormData={setFormData} />;
      case 7:
        return <Step7Qualification formData={formData} setFormData={setFormData} />;
      case 8:
        return <Step8Disqualifiers formData={formData} setFormData={setFormData} />;
      case 9:
        return <Step9Pricing formData={formData} setFormData={setFormData} />;
      case 10:
        return <Step10Tone formData={formData} setFormData={setFormData} errors={errors} />;
      case 11:
        return <Step11Handoff formData={formData} setFormData={setFormData} />;
      case 12:
        return <Step12Legal formData={formData} setFormData={setFormData} />;
      case 13:
        return <Step13Strategy formData={formData} setFormData={setFormData} />;
      default:
        return null;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex items-center justify-center px-6">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-[#00D1FF] animate-spin mx-auto mb-4" />
            <p className="text-[#9CA3AF] font-body">Loading your settings...</p>
          </div>
        </div>
      </div>
    );
  }

  // Configured state
  if ((hasExistingSettings && !isEditMode) || isSubmitted) {
    return (
      <div className="min-h-screen bg-[#0F1115] flex">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex items-center justify-center px-6 py-12">
          <div className="max-w-lg w-full text-center">
            <div className="w-24 h-24 rounded-full bg-[#00D1FF]/20 flex items-center justify-center mx-auto mb-8 animate-pulse">
              <Sparkles className="w-12 h-12 text-[#00D1FF]" />
            </div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-[#F3F4F6] mb-4">
              Settings Received!
            </h1>
            <p className="text-[#9CA3AF] font-body text-lg mb-6">
              Your Digital Brain settings have been saved and will be used to train the assistant.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleEditSettings}
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-[#F3F4F6] font-heading font-semibold hover:bg-white/5 transition-colors"
              >
                <Pencil className="w-5 h-5" />
                Edit Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="roofing-bot-settings-page">
      <Sidebar />

      <div className="flex-1 md:ml-64">
        <main className="pt-8 pb-32 px-6">
          <div className="max-w-2xl mx-auto">
            <StepIndicator steps={steps} currentStep={currentStep} />

            <div className="mb-10">
              <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
            </div>

            <div className="glass rounded-2xl p-6 md:p-10 border border-white/10">
              {renderStep()}
            </div>
          </div>
        </main>

        <div className="fixed bottom-0 left-0 md:left-64 right-0 bg-[#0F1115]/95 backdrop-blur-lg border-t border-white/5 p-4 md:p-6">
          <div className="max-w-2xl mx-auto">
            {errors.submit && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm font-body text-center">
                {errors.submit}
              </div>
            )}

            <div className="flex items-center justify-between gap-4">
              {currentStep > 1 ? (
                <button
                  onClick={handleBack}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-6 py-3 rounded-lg border border-white/20 text-[#F3F4F6] font-heading font-semibold hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="roofing-back-button"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>
              ) : (
                <div />
              )}

              {currentStep < totalSteps ? (
                <button
                  onClick={handleNext}
                  disabled={isNextDisabled() || isSaving}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-heading font-bold transition-all ${
                    isNextDisabled() || isSaving
                      ? "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
                      : "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                  }`}
                  data-testid="roofing-next-button"
                >
                  {isSaving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0F1115]/30 border-t-[#0F1115] rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isNextDisabled() || isSaving}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-heading font-bold transition-all ${
                    isNextDisabled() || isSaving
                      ? "bg-[#00D1FF]/30 text-[#0F1115]/50 cursor-not-allowed"
                      : "btn-pulse bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                  }`}
                  data-testid="roofing-submit-button"
                >
                  {isSaving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-[#0F1115]/30 border-t-[#0F1115] rounded-full animate-spin" />
                      Launching...
                    </>
                  ) : (
                    <>
                      <Rocket className="w-5 h-5" />
                      Launch My Digital Brain
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoofingBotSettings;
